import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { usePendingOrders } from '@/modules/orders/hooks/use-pending-orders';
import { STATUS_BADGE } from '@/modules/orders/constants/status-badge.constant';
import { ChevronRight } from 'lucide-react';

interface PendingOrderCardProps {
  merchantSlug?: string;
  className?: string;
}

export function PendingOrderCard({ merchantSlug, className }: PendingOrderCardProps) {
  const { hasPendingOrders, pendingOrders } = usePendingOrders();
  const [, navigate] = useLocation();

  const relevantOrder = useMemo(() => {
    if (!hasPendingOrders || pendingOrders.length === 0) return null;

    if (merchantSlug) {
      return pendingOrders.find(order => order.merchant_slug === merchantSlug) || null;
    }

    return pendingOrders[0];
  }, [hasPendingOrders, pendingOrders, merchantSlug]);

  if (!relevantOrder) return null;

  const statusBadge = STATUS_BADGE[relevantOrder.status] || STATUS_BADGE[1];

  const handleCardClick = () => {
    navigate(`/orders/${relevantOrder.id}`);
  };

  const getStatusMessage = () => {
    switch (relevantOrder.status) {
      case 1:
        return 'Aguardando confirmação da loja';
      case 2:
        return 'Sendo preparado';
      case 3:
        return 'Saiu para entrega';
      case 7:
        return 'Pronto para retirada';
      case 8:
        return 'Aguardando pagamento';
      default:
        return 'Em andamento';
    }
  };

  const getStatusColor = () => {
    switch (statusBadge?.variant) {
      case 'warning':
        return 'text-yellow-600';
      case 'info':
        return 'text-cyan-600';
      case 'success':
        return 'text-green-600';
      case 'destructive':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div 
      className={`clickable-card cursor-pointer bg-white border border-gray-200 rounded-2xl p-3.5 ${className}`}
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            Pedido em andamento
          </p>
          <p className={`text-xs mt-1 font-medium ${getStatusColor()}`}>
            {getStatusMessage()}
          </p>
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
          <span>Ver</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}